/* Session storage for the RBAC/multi-business API (server v2).
 *
 * Kept free of React and axios imports so both axiosConfig (the refresh
 * interceptor) and AuthContext can use it without a circular import.
 *
 * Access tokens now live 15 minutes, so the refresh token is what actually
 * keeps a user signed in — losing it means a logout on the next request. */

const ACCESS_KEY = "RF-session-token";
const REFRESH_KEY = "RF-refresh-token";
const SESSION_KEY = "RF-session";
const BUSINESS_KEY = "RF-business-id";

const read = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (_) {
    return null;
  }
};

const write = (key, value) => {
  try {
    if (value === null || value === undefined) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch (_) {}
};

export const getAccessToken = () => read(ACCESS_KEY);
export const getRefreshToken = () => read(REFRESH_KEY);

export function saveTokens(accessToken, refreshToken) {
  write(ACCESS_KEY, accessToken);
  // /auth/refresh rotates the pair; a missing value in a response must not
  // wipe a still-valid stored token.
  if (refreshToken) write(REFRESH_KEY, refreshToken);
}

/* { user, business, memberships } — the parts of the login response that
   /auth/verify does not return (it only echoes the token payload). */
export function saveSession(session) {
  try {
    write(SESSION_KEY, JSON.stringify(session));
  } catch (_) {}
}

export function getSession() {
  const raw = read(SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

/* The business the user is currently inside, set when a business is opened
   from the group overview and cleared on exit. While present, every API
   request carries it as a `business-id` header (see axiosConfig). */
export const getActiveBusinessId = () => read(BUSINESS_KEY);

export function setActiveBusinessId(id) {
  write(BUSINESS_KEY, id === null || id === undefined ? null : String(id));
}

export function clearActiveBusinessId() {
  write(BUSINESS_KEY, null);
}

export function clearSession() {
  write(ACCESS_KEY, null);
  write(REFRESH_KEY, null);
  write(SESSION_KEY, null);
  write(BUSINESS_KEY, null);
}

export { ACCESS_KEY, REFRESH_KEY, SESSION_KEY, BUSINESS_KEY };
